/**
 * Живий стан центру сповіщень (notifications.md, розд. 6; NTF-17, NTF-18, NTF-21): лічильник,
 * тост-сповіщення, стан панелі. Без React, крім хуків унизу: події SSE приходять поза деревом.
 *
 * - **Лічильник** — `GET …/count` (1 SQL). Подія `notification.created@1` чи `notification.read@1`
 *   (будь-яка зміна позначок, і з інших вкладок та пристроїв) — перечитати його. Без потоку подій
 *   (обрив, легкий режим до першого екрана) — опитування раз на 60 с.
 * - **Тост** — лише `urgency = high` і не для типу, якому людина чи компанія вимкнули спливне вікно
 *   (рішення сервера — поле `toast` пункту, NTF-26). Пункт бере одна вкладка браузера — лідер
 *   (Web Locks), щоб 46 вкладок не били `GET …/{id}` хвилею, і розсилає його іншим (`BroadcastChannel`). Стек тостів однаковий у всіх вкладках: закрили в одній — зник в усіх;
 *   бачить його та, що на екрані. Згорнута подія (`grouped: true`) оновлює наявний тост, а не
 *   показує новий (розд. 4.2).
 * - **Звук** — рішення сервера в полі `sound` пункту; лідер розсилає сигнал, а грає лише остання
 *   активна вкладка (`sound-owner.ts`; тон — лінивий чанк `sound.ts`, NTF-19). Згорнута подія звуку
 *   не дає: 5 лідів за хвилину — один.
 * - **Панель відкрита** — нове не вставляється в список під курсором: росте банер «↑ N нових».
 * - **Лідер — лише з живим потоком.** Вкладка без потоку подій (обрив, сторінка в кеші «назад /
 *   вперед», легкий режим до першого екрана) подій не отримує, тож і замок не тримає: інакше тост
 *   не прийшов би нікому. Потік закрився — замок відпущено; відкрився — запит знову. Так само й
 *   bfcache: потік закривається на `pagehide` з `persisted`, і сторінка без замка потрапляє в кеш
 *   (UX-34).
 * - **Потік знову відкрився після обриву** — лічильник перечитується: події між з'єднаннями могли
 *   загубитись.
 */
import type { NotificationView, RealtimeEvent } from '@clientline/contracts';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';
import { useFirstScreenDone } from '../../perf/first-screen.ts';
import { isStreamOpen, onNotificationEvent, onStreamState } from '../../platform/events.ts';
import { QUIET } from '../../platform/query-meta.ts';
import { fetchNotification, fetchUnreadCount, notificationKeys } from './api.ts';
import { isSoundOwner, trackSoundOwner } from './sound-owner.ts';

/** Межа числа в дзвіночку й заголовку (UX-02, як «Задачі N»): далі — «99+». */
export const COUNT_CAP = 99;
/** Видно не більше трьох тостів-сповіщень; решта — «Ще N у центрі» (states.md, розд. 9). */
export const TOAST_STACK_MAX = 3;
/** Опитування лічильника, коли потоку подій немає. */
export const COUNT_POLL_MS = 60_000;

const LOCK = 'cl:notifications-leader';
const CHANNEL = 'cl:notifications';

export const countText = (n: number) => (n > COUNT_CAP ? `${COUNT_CAP}+` : String(n));

// ---------------------------------------------------------------- стан

interface LiveState {
  toasts: readonly NotificationView[];
  panelOpen: boolean;
  /** Скільки подій `created` прийшло, поки панель відкрита (банер «↑ N нових»). */
  newWhileOpen: number;
}

let state: LiveState = { toasts: [], panelOpen: false, newWhileOpen: 0 };
const listeners = new Set<() => void>();

function set(next: Partial<LiveState>) {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getState = () => state;

/** Поточний стан без підписки (обробники подій). */
export const getNotificationsLive = getState;

// ---------------------------------------------------------------- тости

type Message =
  | { t: 'show'; item: NotificationView; grouped: boolean }
  | { t: 'dismiss'; id: string }
  | { t: 'subject'; entity: string; id: string }
  | { t: 'all' }
  | { t: 'sound' };

let channel: BroadcastChannel | null = null;

function broadcast(message: Message) {
  try {
    channel?.postMessage(message);
  } catch {
    // Канал закрито (вкладка йде): інші вкладки дізнаються з наступної події.
  }
}

function apply(message: Message) {
  switch (message.t) {
    case 'show': {
      const { item, grouped } = message;
      const same = state.toasts.findIndex((toast) => toast.id === item.id);
      if (same >= 0) {
        set({ toasts: state.toasts.map((toast, i) => (i === same ? item : toast)) });
      } else if (!grouped) {
        // Новий — зверху (states.md, розд. 9). Згорнута подія закритого тосту не повертає його:
        // пункт і так у центрі, а сигнал на кожну подію групи — те, від чого згортання рятує.
        set({ toasts: [item, ...state.toasts] });
      }
      return;
    }
    case 'dismiss':
      set({ toasts: state.toasts.filter((toast) => toast.id !== message.id) });
      return;
    case 'subject':
      set({
        toasts: state.toasts.filter(
          (toast) => !(toast.subject?.entity === message.entity && toast.subject.id === message.id),
        ),
      });
      return;
    case 'all':
      if (state.toasts.length > 0) set({ toasts: [] });
      return;
    case 'sound':
      if (!isSoundOwner()) return;
      void import('./sound.ts').then(
        (sound) => sound.playNotificationSound(),
        () => {
          // Чанк не завантажився: без звуку, тост і пункт у центрі є.
        },
      );
      return;
  }
}

/** Зміна стеку в цій вкладці й в усіх інших. */
function everywhere(message: Message) {
  apply(message);
  broadcast(message);
}

export const dismissNotificationToast = (id: string) => everywhere({ t: 'dismiss', id });

/** Пункти запису прочитано (відкрито картку): їхні тости більше не потрібні. */
export const dismissSubjectToasts = (entity: string, id: string) =>
  everywhere({ t: 'subject', entity, id });

/** «Прочитати все»: тости теж. */
export const dismissAllNotificationToasts = () => everywhere({ t: 'all' });

// ---------------------------------------------------------------- панель

export function setCenterOpen(open: boolean) {
  if (open === state.panelOpen) return;
  set({ panelOpen: open, newWhileOpen: 0 });
}

export const resetNewWhileOpen = () => {
  if (state.newWhileOpen > 0) set({ newWhileOpen: 0 });
};

// ---------------------------------------------------------------- лідер вкладок

let leader = false;

/**
 * Лідерство за станом потоку цієї вкладки: `true` — просити замок, `false` — відпустити. Без Web
 * Locks кожна вкладка з потоком — сама собі лідер: стек однаковий і так, бо пункт з тим самим `id`
 * не дублюється.
 */
function leadership(): { want: (on: boolean) => void; stop: () => void } {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  let release: (() => void) | null = null;
  let pending: AbortController | null = null;
  const letGo = () => {
    pending?.abort();
    pending = null;
    release?.();
    leader = false;
  };
  const acquire = () => {
    if (!locks) {
      leader = true;
      return;
    }
    if (release || pending) return;
    const abort = new AbortController();
    pending = abort;
    locks
      .request(
        LOCK,
        { signal: abort.signal },
        () =>
          new Promise<void>((resolve) => {
            pending = null;
            leader = true;
            release = () => {
              leader = false;
              release = null;
              resolve();
            };
          }),
      )
      .catch(() => {
        if (pending === abort) pending = null;
      });
  };
  return { want: (on) => (on ? acquire() : letGo()), stop: letGo };
}

// ---------------------------------------------------------------- події

/** Подія потоку → лічильник, банер панелі, тост (експорт — для тестів). */
export function applyNotificationEvent(client: QueryClient, event: RealtimeEvent) {
  void client.invalidateQueries({ queryKey: notificationKeys.count });
  if (event.type !== 'notification.read' && event.type !== 'notification.created') return;
  if (state.panelOpen) {
    // Відкриту панель не перебудовуємо під людиною: нове — банером, прочитане деінде — при
    // наступному відкритті.
    if (event.type === 'notification.created') set({ newWhileOpen: state.newWhileOpen + 1 });
    void client.invalidateQueries({ queryKey: notificationKeys.lists, refetchType: 'none' });
  } else {
    // Закрита панель: старий кеш не показуємо, бо свіжа сторінка вставила б нове над рядком, на
    // який уже поставлено фокус. Відкриття почне з чистої першої сторінки.
    client.removeQueries({ queryKey: notificationKeys.lists });
  }
  if (event.type !== 'notification.created') return;
  const id = event.data?.notificationId;
  if (event.data?.urgency !== 'high' || typeof id !== 'string' || !leader) return;
  const grouped = event.data.grouped === true;
  // Згорнута подія без тосту на екрані — нічого не показуємо (apply), тож і запит зайвий.
  if (grouped && !state.toasts.some((toast) => toast.id === id)) return;
  fetchNotification(id).then(
    (item) => {
      if (item.readAt !== null) return;
      // `toast: false` — людина чи компанія вимкнули спливне вікно типу (NTF-26): лише центр.
      if (item.toast !== false) everywhere({ t: 'show', item, grouped });
      if (item.sound === true && !grouped) everywhere({ t: 'sound' });
    },
    () => {
      // 404 (уже очищено) чи збій: пункт лишається в центрі, лічильник уже оновлено.
    },
  );
}

/** Підписка вкладки: події SSE, канал між вкладками, лідерство. Повертає відписку. */
export function startNotificationsLive(client: QueryClient): () => void {
  const stopEvents = onNotificationEvent((event) => applyNotificationEvent(client, event));
  // Чанк тону — на жест, а не на старт: легкий режим після `cl:list-ready` скриптів не просить.
  const stopOwner = trackSoundOwner(() => {
    void import('./sound.ts').then(
      (sound) => sound.armSound(),
      () => undefined,
    );
  });
  const openChannel = () => {
    if (typeof BroadcastChannel === 'undefined' || channel) return;
    channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (message: MessageEvent<Message>) => {
      if (message.data && typeof message.data === 'object') apply(message.data);
    };
  };
  openChannel();
  // Повідомлення каналу, поки сторінка в bfcache, викидає її з кешу (#1257): канал закриваємо на час
  // кешу; пропущене дожене лічильник, коли потік відкриється знову.
  const closeChannel = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    channel?.close();
    channel = null;
  };
  const reopenChannel = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    openChannel();
    // Поки сторінка була в кеші, стек могли закрити в іншій вкладці; пункти лишаються в центрі.
    set({ toasts: [] });
  };
  window.addEventListener('pagehide', closeChannel);
  window.addEventListener('pageshow', reopenChannel);
  const lead = leadership();
  let wasOpen = isStreamOpen();
  lead.want(wasOpen);
  let opened = wasOpen;
  const stopStream = onStreamState(() => {
    const open = isStreamOpen();
    if (open === wasOpen) return;
    wasOpen = open;
    lead.want(open);
    // Потік відкрився знову після обриву: події між з'єднаннями могли загубитись.
    if (open && opened) {
      void client.invalidateQueries({ queryKey: notificationKeys.count });
      if (!state.panelOpen) client.removeQueries({ queryKey: notificationKeys.lists });
    }
    if (open) opened = true;
  });
  return () => {
    stopEvents();
    stopOwner();
    stopStream();
    lead.stop();
    window.removeEventListener('pagehide', closeChannel);
    window.removeEventListener('pageshow', reopenChannel);
    channel?.close();
    channel = null;
    set({ toasts: [], panelOpen: false, newWhileOpen: 0 });
  };
}

// ---------------------------------------------------------------- хуки

export const useNotificationsLive = () => useSyncExternalStore(subscribe, getState, getState);

const streamOpen = () => isStreamOpen();

/**
 * Число непрочитаних; `undefined` — ще не відомо (чи немає права). Запит — після першого екрана
 * (`perf/first-screen.ts`, web-speed.md): у бюджеті запитів першого екрана лічильника немає.
 */
export function useUnreadCount(): number | undefined {
  const live = useSyncExternalStore(onStreamState, streamOpen, streamOpen);
  const ready = useFirstScreenDone();
  const { data } = useQuery({
    queryKey: notificationKeys.count,
    queryFn: ({ signal }) => fetchUnreadCount(signal),
    enabled: ready,
    refetchInterval: live ? false : COUNT_POLL_MS,
    // Збій лічильника не варто оголошувати: дзвіночок просто без числа, наступна подія спробує ще.
    meta: QUIET,
  });
  return data;
}

/** Скинути стан між тестами. */
export function resetNotificationsLive() {
  state = { toasts: [], panelOpen: false, newWhileOpen: 0 };
  leader = false;
  channel?.close();
  channel = null;
}
